import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";

import moment from "moment";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import {
  useChangeTutorialStatusMutation,
  useDeleteTutorialMutation,
  useLazyGetTutorialByIdQuery,
  useLazyGetTutorialsQuery,
} from "../../services/tutorial";
import { Loader, showError, showToast } from "../../constants";
import WarnModal from "../../components/WarnModal";
import Pagination from "../../components/Pagination";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import useTranslation from "../../hooks/Translation";
import useAuth from "../../hooks/useAuth";
import SkeletonTable from "../../components/SkeletonTable";

const ManageTutorial = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [deleteTutorial] = useDeleteTutorialMutation();
  const [getTutorials] = useLazyGetTutorialsQuery();
  const [tutorialStatus] = useChangeTutorialStatusMutation();
  //   const [deleteBannerById] = useLazyDeleteBannerIdQuery();

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [tutorialData, setTutorialData] = useState<any>();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const translate = useTranslation();
  const [hidePermission, setHidePermission] = useState<any>({
    isAdd: true,
    isDelete: true,
  });
  let totalPages = Math.ceil(totalCount / 10);

  const label = { inputProps: { "aria-label": "Switch demo" } };
  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getSubAdmin = async () => {
    setLoading(true);
    try {
      const response = await getTutorials({
        page: page,
        query: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        setTutorialData(response?.data?.tutorial || []);
        setTotalCount(response?.data?.count);
      } else {
        setTutorialData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (id: string, status: boolean) => {
    const data = {
      isBlocked: status,
    };

    let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;

    const response = await tutorialStatus({
      encryptedBody,
      tutorialId: id,
    }).unwrap();
    if (response?.statusCode === 200) {
      const updatedData = tutorialData?.map((x: { _id: string }) => {
        if (x._id === id) {
          return {
            ...x,
            isBlocked: status,
          };
        }
        return x;
      });
      setTutorialData(updatedData);
      showToast(translate?.error?.statusUpdate || "");
    } else {
      showError(translate?.error?.pleaseTryAgain || "");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteTutorial({ tutorialId: id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(translate?.error?.tutorialDel || "");
        getSubAdmin();
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const checkPermission = () => {
    const permissions = userData?.permission;
    if (permissions?.length) {
      let idx = -1;
      idx = permissions?.findIndex(
        (ele: any) => ele?.name === "Promotional Tutorials"
      );

      if (idx != -1) {
        let data = {
          isAdd: permissions[idx]?.isAdd,
          isDelete: permissions[idx]?.isDelete,
        };
        setHidePermission(data);
      } else {
        setHidePermission({ isAdd: false, isDelete: false });
      }
    }
  };

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    getSubAdmin();
  }, [debouncedSearchTerm, page]);

  return (
    <MainContainer>
      <Loader isLoad={loading} isLinear />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.main?.ManageTutorials}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Box className="cards_header_right">
              {hidePermission?.isAdd ? (
                <Button
                  className="btn btn_primary"
                  onClick={() => navigate("/manage-tutorials/add")}
                >
                  {translate?.main?.AddTutorial}
                </Button>
              ) : null}
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table className="tutorial-table" sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{translate?.main?.SNo}</TableCell>
                  <TableCell align="center">
                    {translate?.main?.VideoTitle}
                  </TableCell>
                  <TableCell align="center">
                    {translate?.main?.Description}
                  </TableCell>

                  {hidePermission?.isAdd ? (
                    <TableCell align="center" className="tutorial-status-cell">
                      {translate?.main?.Status}
                    </TableCell>
                  ) : null}
                  {(hidePermission?.isAdd || hidePermission?.isDelete) ? (
                    <TableCell align="center" className="tutorial-action-cell table-action-cell">
                      {translate?.main?.Action}
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTable columns={5} rows={10} />
                ) : tutorialData?.length ? (
                  tutorialData?.map((row: any, i: any) => (
                    <TableRow key={row?._id}>
                      <TableCell align="center">
                        {(page - 1) * 10 + i + 1}
                      </TableCell>
                      <TableCell align="center">
                        {row?.title || "-"}
                      </TableCell>
                      <TableCell align="center">
                        {row?.description || "-"}
                      </TableCell>

                      {/* <TableCell align="center">20/09/2023</TableCell>
                <TableCell align="center">30/09/2023</TableCell> */}
                      {hidePermission?.isAdd ? (
                        <TableCell align="center" className="tutorial-status-cell">
                          <Box
                            className="tutorial-status-switch"
                            onClick={(event) => event.stopPropagation()}
                            onTouchEnd={(event) => event.stopPropagation()}
                          >
                            <Switch
                              {...label}
                              size="small"
                              checked={!row?.isBlocked}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                event.stopPropagation();
                                updateUserStatus(row?._id, !row?.isBlocked);
                              }}
                            />
                          </Box>
                        </TableCell>
                      ) : null}
                      {(hidePermission?.isAdd || hidePermission?.isDelete) ? (
                        <TableCell
                          align="center"
                          className="tutorial-action-cell table-action-cell"
                        >
                          <Box
                            className="table_actions"
                            onClick={(event) => event.stopPropagation()}
                            onTouchEnd={(event) => event.stopPropagation()}
                          >
                            {hidePermission?.isAdd ? (
                              <IconButton
                                aria-label="Edit tutorial"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(`/manage-tutorials/edit/${row?._id}`);
                                }}
                              >
                                <ModeEditIcon />
                              </IconButton>
                            ) : null}
                            {hidePermission?.isDelete ? (
                              <IconButton
                                aria-label="Delete tutorial"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpen(true);
                                  setSelectedId(row?._id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={6}>
                      {translate?.main?.NoTutorialFound}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {tutorialData?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={tutorialData}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        ) : (
          ""
        )}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDelete(selectedId)}
        name="Tutorial"
      />
    </MainContainer>
  );
};

export default ManageTutorial;
